import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { UnidadeMedidaDeItemDTO } from '../../../models/configuracoes/unidade-de-medida.model';
import { UnidadeMedidaDeItemService } from '../../../services/configuracoes/unidade-de-medida.service';

@Component({
  selector: 'app-unidade-adicionar-dialog',
  templateUrl: './unidade-adicionar-dialog.component.html',
  styleUrls: ['./unidade-adicionar-dialog.component.scss'],
})
export class UnidadeAdicionarDialogComponent implements OnChanges, OnInit, OnDestroy {

  // === INPUTS ===
  @Input() visible = false;

  // === OUTPUTS ===
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() unidadeCriada = new EventEmitter<any>();

  // === ESTADO INTERNO ===
  unidadesDisponiveis: UnidadeMedidaDeItemDTO[] = [];
  unidadeSelecionada: UnidadeMedidaDeItemDTO | null = null;
  carregando: boolean = false;
  salvando: boolean = false;

  // === TRADUCOES ===
  texts: {
    titulo: string;
    subtitulo: string;
    labels_unidade: string;
    placeholders_selecione_unidade: string;
    botoes_fechar: string;
    botoes_vincular: string;
    mensagens_selecione_unidade: string;
    mensagens_unidade_vinculada: string;
    mensagens_erro_vincular: string;
    mensagens_erro_carregar: string;
    mensagens_nenhuma_disponivel: string;
  } = {
    titulo: '',
    subtitulo: '',
    labels_unidade: '',
    placeholders_selecione_unidade: '',
    botoes_fechar: '',
    botoes_vincular: '',
    mensagens_selecione_unidade: '',
    mensagens_unidade_vinculada: '',
    mensagens_erro_vincular: '',
    mensagens_erro_carregar: '',
    mensagens_nenhuma_disponivel: '',
  };

  private langSub?: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private unidadeService: UnidadeMedidaDeItemService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.resetar();
      this.carregarUnidadesDisponiveis();
    }
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  // === METODOS PUBLICOS ===

  /** Submete a vinculacao da unidade selecionada */
  submit(): void {
    if (!this.unidadeSelecionada) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.texts.mensagens_selecione_unidade,
      });
      return;
    }

    this.salvando = true;

    const unidadeId = this.unidadeSelecionada.id;

    this.unidadeService.post(`/unidaDeMedidaDeItemEmpresa?id=${unidadeId}`, undefined).subscribe({
      next: (resultado: any) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.texts.mensagens_unidade_vinculada,
        });
        // Emitir com dados uteis para o componente pai (id e nome)
        this.unidadeCriada.emit({
          id: resultado?.unidadeMedidaDeItemEmpresa?.id || this.unidadeSelecionada!.id,
          nome: resultado?.unidadeMedidaDeItemEmpresa?.nome || this.unidadeSelecionada!.nome,
          unidadeMedidaDeItemEmpresa: resultado?.unidadeMedidaDeItemEmpresa || this.unidadeSelecionada,
        });
        this.resetar();
        this.fechar();
      },
      error: () => {
        this.salvando = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.texts.mensagens_erro_vincular,
        });
      },
    });
  }

  /** Fecha o dialog */
  fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  // === METODOS PRIVADOS ===

  /** Carrega unidades de medida disponiveis (nao cadastradas na empresa) */
  private carregarUnidadesDisponiveis(): void {
    this.carregando = true;
    this.unidadeService.listarUnidadesAdicionaveis().subscribe({
      next: (unidades: UnidadeMedidaDeItemDTO[]) => {
        this.unidadesDisponiveis = unidades || [];
        this.carregando = false;
      },
      error: () => {
        this.carregando = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.texts.mensagens_erro_carregar,
        });
      },
    });
  }

  /** Limpa o estado do formulario */
  private resetar(): void {
    this.unidadeSelecionada = null;
    this.unidadesDisponiveis = [];
    this.salvando = false;
    this.carregando = false;
  }

  /** Carrega traducoes do namespace select-unidade.dialog */
  private loadTranslations(): void {
    const prefix = 'select-unidade.dialog';
    this.texts = {
      titulo: this.translate.instant(`${prefix}.titulo`),
      subtitulo: this.translate.instant(`${prefix}.subtitulo`),
      labels_unidade: this.translate.instant(`${prefix}.labels.unidade`),
      placeholders_selecione_unidade: this.translate.instant(`${prefix}.placeholders.selecione_unidade`),
      botoes_fechar: this.translate.instant(`${prefix}.botoes.fechar`),
      botoes_vincular: this.translate.instant(`${prefix}.botoes.vincular`),
      mensagens_selecione_unidade: this.translate.instant(`${prefix}.mensagens.selecione_unidade`),
      mensagens_unidade_vinculada: this.translate.instant(`${prefix}.mensagens.unidade_vinculada`),
      mensagens_erro_vincular: this.translate.instant(`${prefix}.mensagens.erro_vincular`),
      mensagens_erro_carregar: this.translate.instant(`${prefix}.mensagens.erro_carregar`),
      mensagens_nenhuma_disponivel: this.translate.instant(`${prefix}.mensagens.nenhuma_disponivel`),
    };
  }
}
